import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FileSearch, ChevronDown, Building2 } from 'lucide-react';
import './DiligenceOverviewTable.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Default category order
const DEFAULT_CATEGORY_ORDER = ['Financial', 'Legal', 'Tax', 'Operational', 'HR', 'IT'];

function DiligenceOverviewTable({ onViewDiligenceTracker, onSelectDeal }) {
  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [diligenceData, setDiligenceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeals();
  }, []);

  useEffect(() => {
    if (selectedDealId) {
      fetchDiligenceData(selectedDealId);
    }
  }, [selectedDealId]);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/deals`);
      const dealsData = response.data || [];
      setDeals(dealsData);
      
      if (dealsData.length > 0) {
        setSelectedDealId(dealsData[0].id);
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDiligenceData = async (dealId) => {
    try {
      const response = await axios.get(`${API_URL}/api/deals/${dealId}/diligence`);
      setDiligenceData(response.data);
    } catch (error) {
      console.error('Error fetching diligence data:', error);
      setDiligenceData(null);
    }
  };

  // Calculate category stats
  const categoryRows = useMemo(() => {
    if (!diligenceData?.items || !diligenceData?.categories) return [];

    const { items, categories } = diligenceData;

    // Sort categories: default order first, then custom by order field
    const sortedCategories = [...categories].sort((a, b) => {
      const aIndex = DEFAULT_CATEGORY_ORDER.indexOf(a.name);
      const bIndex = DEFAULT_CATEGORY_ORDER.indexOf(b.name);
      
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      return (a.order || 0) - (b.order || 0);
    });

    return sortedCategories.map(category => {
      const categoryItems = items.filter(item => item.category === category.name);
      const open = categoryItems.filter(i => i.status === 'Open').length;
      const partial = categoryItems.filter(i => i.status === 'Partial').length;
      const closed = categoryItems.filter(i => i.status === 'Closed').length;
      const total = categoryItems.length;
      const completion = total > 0 ? Math.round((closed / total) * 100) : 0;

      return {
        id: category.id,
        name: category.name,
        color: category.color,
        open,
        partial,
        closed,
        total,
        completion
      };
    }).filter(row => row.total > 0); // Only show categories with items
  }, [diligenceData]);

  // Calculate total row
  const totalRow = useMemo(() => {
    if (!categoryRows.length) return null;
    
    const open = categoryRows.reduce((sum, row) => sum + row.open, 0);
    const partial = categoryRows.reduce((sum, row) => sum + row.partial, 0);
    const closed = categoryRows.reduce((sum, row) => sum + row.closed, 0);
    const total = categoryRows.reduce((sum, row) => sum + row.total, 0);
    const completion = total > 0 ? Math.round((closed / total) * 100) : 0;

    return { open, partial, closed, total, completion };
  }, [categoryRows]);

  const handleRowClick = (categoryName) => {
    if (!selectedDealId || !onViewDiligenceTracker) return;
    
    const selectedDeal = deals.find(d => d.id === selectedDealId);
    if (!selectedDeal) return;

    // Store selected category in localStorage for the tracker to pick up
    if (categoryName) {
      localStorage.setItem('selectedDiligenceCategory', categoryName);
    } else {
      localStorage.removeItem('selectedDiligenceCategory');
    }

    if (onSelectDeal) {
      onSelectDeal(selectedDeal);
    }
    onViewDiligenceTracker();
  };

  if (loading) {
    return (
      <div className="dov-container">
        <div className="dov-loading">
          <div className="dov-spinner"></div>
        </div>
      </div>
    );
  }

  if (deals.length === 0) {
    return (
      <div className="dov-container">
        <div className="dov-empty">
          <Building2 size={32} />
          <p>No deals available</p>
          <span>Create a deal to view diligence status</span>
        </div>
      </div>
    );
  }

  const hasItems = diligenceData?.items?.length > 0;

  return (
    <div className="dov-container">
      {/* Header with Deal Selector */}
      <div className="dov-header">
        <div className="dov-title-section">
          <FileSearch size={20} />
          <h2>Diligence Status Overview</h2>
        </div>
        <div className="dov-selector">
          <select 
            value={selectedDealId || ''}
            onChange={(e) => setSelectedDealId(e.target.value)}
          >
            {deals.map(deal => (
              <option key={deal.id} value={deal.id}>
                {deal.name}
              </option>
            ))}
          </select>
          <ChevronDown size={16} className="dov-selector-icon" />
        </div>
      </div>

      {!hasItems ? (
        <div className="dov-empty-state">
          <p>No diligence tracker items created for this deal.</p>
        </div>
      ) : (
        <div className="dov-table-wrapper">
          <table className="dov-table">
            <thead>
              <tr>
                <th className="dov-col-category">Category</th>
                <th className="dov-col-number">Open</th>
                <th className="dov-col-number">Partial</th>
                <th className="dov-col-number">Closed</th>
                <th className="dov-col-number">Total</th>
                <th className="dov-col-completion">Completion</th>
              </tr>
            </thead>
            <tbody>
              {categoryRows.map(row => (
                <tr 
                  key={row.id} 
                  className="dov-row"
                  onClick={() => handleRowClick(row.name)}
                  style={{ borderLeftColor: row.color }}
                >
                  <td className="dov-col-category">
                    <span className="dov-category-name">{row.name}</span>
                  </td>
                  <td className="dov-col-number">
                    <span className={`dov-number ${row.open > 0 ? 'dov-open' : ''}`}>
                      {row.open}
                    </span>
                  </td>
                  <td className="dov-col-number">
                    <span className={`dov-number ${row.partial > 0 ? 'dov-partial' : ''}`}>
                      {row.partial}
                    </span>
                  </td>
                  <td className="dov-col-number">
                    <span className={`dov-number ${row.closed > 0 ? 'dov-closed' : ''}`}>
                      {row.closed}
                    </span>
                  </td>
                  <td className="dov-col-number">
                    <span className="dov-number dov-total">{row.total}</span>
                  </td>
                  <td className="dov-col-completion">
                    <div className="dov-completion-cell">
                      <span className="dov-completion-text">{row.completion}%</span>
                      <div className="dov-progress-bar">
                        <div 
                          className="dov-progress-fill"
                          style={{ width: `${row.completion}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
              
              {totalRow && (
                <tr 
                  className="dov-row dov-row-total"
                  onClick={() => handleRowClick(null)}
                >
                  <td className="dov-col-category">
                    <span className="dov-category-name">TOTAL</span>
                  </td>
                  <td className="dov-col-number">
                    <span className="dov-number">{totalRow.open}</span>
                  </td>
                  <td className="dov-col-number">
                    <span className="dov-number">{totalRow.partial}</span>
                  </td>
                  <td className="dov-col-number">
                    <span className="dov-number">{totalRow.closed}</span>
                  </td>
                  <td className="dov-col-number">
                    <span className="dov-number dov-total">{totalRow.total}</span>
                  </td>
                  <td className="dov-col-completion">
                    <div className="dov-completion-cell">
                      <span className="dov-completion-text dov-completion-total">
                        {totalRow.completion}%
                      </span>
                      <div className="dov-progress-bar">
                        <div 
                          className="dov-progress-fill dov-progress-total"
                          style={{ width: `${totalRow.completion}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DiligenceOverviewTable;
